import { useState } from "react";

export interface UseApiReturn<Request, Response> {
  trigger: (request: Request) => Promise<Response>;
  isLoading: boolean;
  isSuccess: boolean;
  isCompleted: boolean;

  response: Response | null;
  error: Error | string;
  reset: () => void;
}

export function useApi<Request, Response>(
  clientFunction: (request: Request) => Promise<Response>,
  props: {
    onSuccess?: (response: Response) => void;
    onError?: (error: Error | string) => void;
    onFinally?: () => void;
    keepPrevious: boolean;
  } = {
    keepPrevious: false,
  }
): UseApiReturn<Request, Response> {
  const { onSuccess, onError, onFinally } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [response, setResponse] = useState<Response | null>(null);
  const [error, setError] = useState<Error | string>(undefined);

  const trigger = async (request: Request): Promise<Response> => {
    try {
      setIsCompleted(false);
      setIsLoading(true);
      if (!props.keepPrevious) setResponse(null);
      setError(undefined);
      const response = await clientFunction(request);
      setResponse(response);

      onSuccess?.(response);
      return response;
    } catch (error) {
      setError(error as Error);
      onError?.(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
      setIsCompleted(true);
      onFinally?.();
    }
  };

  const reset = () => {
    setIsLoading(false);
    setIsCompleted(false);
    setResponse(null);
    setError(undefined);
  };

  return {
    trigger,
    isLoading,
    response,
    error,
    isCompleted,
    isSuccess: !error && isCompleted,
    reset,
  };
}
